import { useEffect, useRef } from 'react';

import { minUtrValue, maxUtrValue } from '../../../../../utils/constants';
import { parseDistanceMi } from '../../../../../utils/distance';
import { CONFIDENCE_HIGH, didNaturalSearchTransition } from '../../../../../utils/naturalSearch';
import { FILTER_VALUES } from '../../constants';

const { EVENTS } = FILTER_VALUES;

/**
 * Bridge natural-language search results into V2's local filter state.
 *
 * V2 owns its filters via useEventFilters() (plain useState), so it does
 * not observe the Redux mutations the NL thunk performs on
 * state.search.filters. This hook watches state.naturalSearch.appliedSentinel
 * — the NL thunk's final dispatch — and, on each apply, copies the bridged
 * filters into V2's local state and kicks off a search.
 *
 * Gated on appliedSentinel, not confidence/query alone:
 * confidence/query change after FETCH_SUCCESS, but state.search.filters
 * isn't updated until UPDATE_FILTERS_APPLIED two dispatches later — firing
 * earlier captures stale filters.
 *
 * The bridge body reads naturalSearch/searchFilters/replaceFilters/searchEvents
 * but the effect intentionally depends only on appliedSentinel — adding the
 * others would re-fire the effect on every render of the V2 page. To keep the
 * read fresh without widening the dep list, the body reads from a ref that is
 * synced each render.
 */
export function useNaturalSearchBridge({
    naturalSearch,
    searchFilters,
    replaceFilters,
    searchEvents,
}) {
    const latestRef = useRef({ naturalSearch, searchFilters, replaceFilters, searchEvents });
    latestRef.current = { naturalSearch, searchFilters, replaceFilters, searchEvents };

    const appliedSentinel = naturalSearch?.appliedSentinel ?? 0;
    // Seed to 0, NOT appliedSentinel: when V2 mounts *because* an NL search just
    // switched the type to events, appliedSentinel is already > 0 and
    // EventSearchResults' mount-search effect deliberately skips — so the bridge
    // is what runs that first NL search. Seeding to appliedSentinel would make
    // didNaturalSearchTransition false on that mount and nothing would search.
    // Tradeoff: a redundant re-bridge if V2 unmounts/remounts while an old NL
    // apply is still in Redux — harmless, it re-applies the same filters.
    const bridgedSentinelRef = useRef(0);

    useEffect(() => {
        if (!didNaturalSearchTransition(bridgedSentinelRef.current, appliedSentinel)) {
            return;
        }
        const {
            naturalSearch: ns,
            searchFilters: sf,
            replaceFilters: rf,
            searchEvents: se,
        } = latestRef.current;
        if (
            ns?.confidence !== CONFIDENCE_HIGH ||
            !ns?.query ||
            sf?.type !== EVENTS
        ) {
            return;
        }
        bridgedSentinelRef.current = appliedSentinel;
        const v2Filters = { ...sf };
        if (typeof v2Filters.distance === 'string') {
            const n = parseDistanceMi(v2Filters.distance);
            if (n != null) {
                v2Filters.distance = n;
            }
        }
        // INITIAL_FILTERS bleeds player-only defaults into our LLM filters via
        // REPLACE_FILTER_INPUTS. The V2 events backend rejects events that don't
        // satisfy utrFitPosition (a player-search concept), so strip it. Also
        // strip the universe-spanning UTR defaults so the backend doesn't treat
        // them as constraints; if the LLM had wanted a UTR range it would have
        // emitted explicit min/max.
        delete v2Filters.utrFitPosition;
        if (v2Filters.utrMin === minUtrValue && v2Filters.utrMax === maxUtrValue) {
            delete v2Filters.utrMin;
            delete v2Filters.utrMax;
        }
        rf(v2Filters);
        se(v2Filters, v2Filters.query || '');
        // Intentionally only depends on appliedSentinel — naturalSearch /
        // searchFilters / replaceFilters / searchEvents are read from
        // latestRef so the effect fires only on a new NL apply, not on
        // every render of the V2 page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedSentinel]);
}
